import { Bar, ComposedChart, Label, Line, Tooltip, XAxis, YAxis } from 'recharts';
import { format, parseISO } from 'date-fns';
import React, { memo, useRef } from 'react';
import theme from 'styles/theme';
import { ChartTooltip } from 'components/common/Charts/ChartToolTip';
import { ChartLegend } from 'components/Charts/ChartLegend';
import { chartStyles } from 'components/RegimeChange/styles';
import { useBoolean } from 'hooks/useBoolean';
import { IInstrument } from 'components/common/Charts/types';
import Spinner from 'components/common/Spinner';
import { Chart } from 'components/RegimeChange/Chart';
import { CSVIMGExport } from 'components/common/DataExport/CSVIMGExport';
import { DataExportRow } from 'components/common/DataExport/styles';
import { Col } from 'react-grid-system';
import { useRegimeChangeApi } from '../useRegimeChangeApi';
import { IGraph } from '../types';
import { ChartTitle } from '../ChartTitle';

interface IFrequency3Month extends Omit<IGraph, 'options'> {
  label: string;
}

export const Frequency3Months: React.FC<IFrequency3Month> = memo(({ instrument, label, instrumentVariants }) => {
  const { useFrequencyData } = useRegimeChangeApi();
  const [isMarkowState1Visible, toggleMarkowState1] = useBoolean(true);
  const [isMarkowState2Visible, toggleMarkowState2] = useBoolean(true);
  const [isPercentChangeVisible, togglePercentChange] = useBoolean(true);

  const { data, isValidating, error } = useFrequencyData({
    instrument: instrument?.value,
    period: 65,
    key: 'get_3month_frequency_data',
  });
  const chartRef = useRef(null);

  if (error) return <div>Frequency three months chart - Error has occurred</div>;
  if (isValidating || !data) return <Spinner />;
  if (!data.data.length) return null;

  const toolTipLabels = {
    bull: 'Number of State 1 Signals above 0.8: ',
    bear: 'Number of State 2 Signals above 0.8: ',
    priceChanges: `${label} ${format(data.minDate, 'MM/dd/yyyy')}:`,
  };

  const instrumentsChartConfig = [
    {
      name: 'bull',
      label: 'Number of State 1 Signals above 0.8',
      color: '#66b3ff',
      isVisible: isMarkowState1Visible,
      onClick: toggleMarkowState1,
      yAxisId: 'left',
    },
    {
      name: 'bear',
      label: 'Number of State 2 Signals above 0.8:',
      color: '#cc0000',
      isVisible: isMarkowState2Visible,
      onClick: toggleMarkowState2,
      yAxisId: 'left',
    },
    {
      name: 'priceChanges',
      label: `${label} ${format(data.minDate, 'MM/dd/yyyy')}`,
      color: theme.palette.brand,
      isVisible: isPercentChangeVisible,
      onClick: togglePercentChange,
      yAxisId: 'right',
    },
  ];

  const chartTitle = '3 Month Rolling Frequency of Strong Markov State 1/State 2 Signal';
  const csvHeading = `Instrument, ${instrument?.label}`;
  const csvData = {
    fields: ['Date', 'Markov State 1', 'Markov State 2', 'Price Change'],
    data: data.csvData,
  };
  return (
    <>
      <DataExportRow justify="end">
        <Col xs={2}>
          <CSVIMGExport data={csvData} fileName={chartTitle} chartRef={chartRef} csvHeading={csvHeading} />
        </Col>
      </DataExportRow>
      <Chart
        ref={chartRef}
        chartName="frequency-3month-chart"
        title={
          <ChartTitle title={chartTitle} instrumentName={instrument?.label} instrumentVariants={instrumentVariants} />
        }
        legend={<ChartLegend instruments={instrumentsChartConfig as IInstrument[]} />}
        chart={
          <ComposedChart data={data.chartData} width={1400} height={300}>
            {isMarkowState1Visible && <Bar dataKey="bull" fill="#66b3ff" yAxisId="left" isAnimationActive={false} />}
            {isMarkowState2Visible && <Bar dataKey="bear" fill="#cc0000" yAxisId="left" isAnimationActive={false} />}
            <Line
              hide={!isPercentChangeVisible}
              dataKey="priceChanges"
              dot={false}
              activeDot={false}
              yAxisId="right"
              stroke={theme.palette.brand}
              type="monotone"
              isAnimationActive={false}
            />
            <YAxis
              yAxisId="left"
              dataKey="frequency"
              domain={[0, data.maxFrequency]}
              tickCount={11}
              tickLine={false}
              axisLine={false}
              dx={-10}
              style={chartStyles.axis}
            >
              <Label
                value="3 Month Rolling Frequency"
                offset={10}
                style={{ fill: 'white', textAnchor: 'middle', fontSize: '0.75rem' }}
                angle={-90}
                position="insideLeft"
              />
            </YAxis>
            <YAxis
              yAxisId="right"
              dataKey="price_change"
              domain={[Math.floor(data.minPriceChange), Math.ceil(data.maxPriceChange / 5) * 5]}
              orientation="right"
              tickLine={false}
              axisLine={false}
              dx={10}
              style={chartStyles.axis}
            >
              <Label
                value={`${label} ${format(data.minDate, 'MM/dd/yyyy')}`}
                offset={10}
                style={{ fill: 'white', textAnchor: 'middle', fontSize: '0.75rem' }}
                angle={90}
                position="insideRight"
              />
            </YAxis>
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickFormatter={value => {
                return value && format(parseISO(value), "MMM ''yy");
              }}
              dy={15}
              dx={30}
              interval={30}
              style={chartStyles.axis}
            />
            <Tooltip
              cursor={{ stroke: theme.palette.brand }}
              content={<ChartTooltip toolTipLabels={toolTipLabels} />}
              isAnimationActive={false}
            />
          </ComposedChart>
        }
      />
    </>
  );
});
